import React, { useCallback, useState } from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import TextInput from "../inputs/TextInput";
import { Button } from "../button";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useGetAPILogFile } from "../../hooks/internal-config/mutation";
import { formatJsonData } from "../../lib/functions";
import TextAreaInput from "../inputs/TextAreaInput";
import DownloadPDFButton from "../button/DownloadPDFButton";

export const ViewLogsConfigCardSchema = Yup.object({
  logFileName: Yup.string().required("Log file name is required"),
  response: Yup.string(),
});

interface FormValues {
  logFileName: string;
  response?: string;
}
const ViewLogsConfigCard = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
  } = useFormWithSchema(ViewLogsConfigCardSchema);

  const logFileName = watch("logFileName");
  const response = watch("response");

  const [isApiLogsDownloading, setIsApiLogsDownloading] = useState(false);

  const { mutateAsync: GetAPILogFile, isPending: isGetAPILogFileLoading } =
    useGetAPILogFile();

  const onSubmit = useCallback(
    async (data: FormValues) => {
      let res;
      try {
        const body = {
          logFileName: data.logFileName,
        };

        res = await GetAPILogFile(body);
      } catch (error: any) {
        res = JSON.stringify(error, null, 2);
      } finally {
        reset({
          logFileName: data.logFileName,
          response: res,
        });
      }
    },
    [GetAPILogFile, reset]
  );

  const handleFormatJSONData = useCallback(() => {
    if (!response) return;

    const formattedData = formatJsonData(response);

    reset({ logFileName, response: formattedData });
  }, [logFileName, reset, response]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-white p-4 rounded-xl shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)] flex flex-col  gap-3 lg:gap-6"
    >
      <h2 className="text-lg lg:text-xl font-medium">View Logs</h2>

      <div className="flex flex-col gap-3 lg:gap-6">
        <div className="grid md:grid-cols-3">
          <Controller
            name="logFileName"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label="Log File Name"
                onChange={onChange}
                value={value || ""}
                error={errors?.[name]?.message}
                required
              />
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button isLoading={isGetAPILogFileLoading}>
            {t("View API Logs")}
          </Button>
        </div>

        <div className="relative">
          <div className="absolute top-0 right-0 z-30 flex gap-2 items-center">
            <DownloadPDFButton
              data={response}
              title={t("Download API Logs")}
              disabled={!response}
              isLoading={isApiLogsDownloading}
              setIsDownloading={setIsApiLogsDownloading}
            />

            <Button
              disabled={!response}
              type="button"
              onClick={handleFormatJSONData}
            >
              {t("Format JSON Data")}
            </Button>
          </div>
          <div className="mt-3">
            <Controller
              name="response"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextAreaInput
                  label="Response"
                  onChange={onChange}
                  value={value || ""}
                  error={errors?.[name]?.message}
                  readOnly
                  rows={15}
                />
              )}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default React.memo(ViewLogsConfigCard);
